import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { NotificationToastPayload, NotificationToastState } from './notification-toast.interface';

/**
 * Drives the single in-app toast shown (via NotificationToastComponent, mounted once globally
 * in app.component.html) when a push notification arrives while the tab is focused --
 * see PushNotificationService.wireLiveBellRefresh()'s onMessage() handler, which calls show()
 * here INSTEAD OF the OS-level Notification API now (that path stays exactly as-is for the
 * background/unfocused-tab case, via firebase-messaging-sw.js's onBackgroundMessage).
 *
 * Per Mihir's spec: a toast slides in from the right, stays up ~5s, then slides back out. If
 * another push arrives while one is already showing, it does NOT stack a second toast -- it
 * aggregates into the same toast ("5 new notifications") and restarts the 5s timer, so a burst
 * of pushes ends with one toast that stays up 5s after the LAST one arrived.
 */
@Injectable({
  providedIn: 'root'
})
export class NotificationToastService {

  /** Public so NotificationToastComponent can drive its progress-bar countdown off the same
   *  number instead of a second, easy-to-drift-out-of-sync constant. */
  static readonly VISIBLE_MS = 5000;

  private stateSubject = new BehaviorSubject<NotificationToastState | null>(null);
  state$ = this.stateSubject.asObservable();

  /** Emits the exact ms remaining whenever a hover-paused toast resumes, so the component can
   *  animate its progress bar from wherever it froze down to 0 over that same span, instead of
   *  restarting from full -- see NotificationToastComponent.onMouseLeave(). */
  private resumeSubject = new Subject<number>();
  resumed$ = this.resumeSubject.asObservable();

  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  /** Wall-clock time the current toast is due to auto-hide -- lets pause() work out exactly how
   *  much time was left without a second, separately-ticking countdown to keep in sync. */
  private hideAt: number | null = null;
  /** Set while hover has paused the auto-hide timer; null the rest of the time. */
  private pausedRemainingMs: number | null = null;

  /**
   * Shows a new toast, or -- for a 'push' arrival while a 'push' toast is already visible --
   * folds this arrival into it and restarts the auto-hide timer (the original aggregation
   * behaviour: a burst of pushes ends up as one toast, not several). An 'away' or 'login' toast
   * (or a 'push' arriving on top of one of those) always REPLACES whatever was showing instead
   * of aggregating -- those counts mean different things (missed-while-hidden / backlog vs.
   * live arrivals) and folding them into one number would be misleading.
   */
  show(payload: NotificationToastPayload, kind: 'push' | 'away' | 'login' = 'push'): void {
    const current = this.stateSubject.value;
    const aggregating = kind === 'push' && current?.kind === 'push';
    const next: NotificationToastState = {
      kind,
      count: aggregating ? current!.count + 1 : 1,
      latestTitle: payload.title,
      latestBody: payload.body,
      latestUrl: payload.url
    };
    this.stateSubject.next(next);
    this.pausedRemainingMs = null; // a fresh arrival always supersedes any earlier pause
    this.armHideTimer(NotificationToastService.VISIBLE_MS);
  }

  /** Slides the toast back out immediately -- close button, click-through navigation, or the
   *  5s timer all funnel through here. */
  dismiss(): void {
    this.clearAutoHide();
    this.pausedRemainingMs = null;
    this.stateSubject.next(null);
  }

  /** Hover-to-pause: freezes the auto-hide timer exactly where it stands. Safe to call when
   *  nothing is running (no toast, or already paused) -- just a no-op then. */
  pause(): void {
    if (this.hideTimer === null || this.hideAt === null) { return; }
    clearTimeout(this.hideTimer);
    this.hideTimer = null;
    this.pausedRemainingMs = Math.max(0, this.hideAt - Date.now());
    this.hideAt = null;
  }

  /** Resumes a hover-paused toast for exactly the time that was left when it was paused --
   *  never a full restart. No-op if nothing is currently paused. */
  resume(): void {
    if (this.pausedRemainingMs === null) { return; }
    const remaining = this.pausedRemainingMs;
    this.pausedRemainingMs = null;
    this.armHideTimer(remaining);
    this.resumeSubject.next(remaining);
  }

  private armHideTimer(ms: number): void {
    this.clearAutoHide();
    this.hideAt = Date.now() + ms;
    this.hideTimer = setTimeout(() => this.dismiss(), ms);
  }

  private clearAutoHide(): void {
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
    this.hideAt = null;
  }
}
